/**
 * Marqueurs sessionStorage OAuth MSAL — sans dépendance à `@azure/msal-browser`
 * (évite d'inclure MSAL dans le bundle des pages publiques).
 */

import type { EmailAlreadyRegisteredNotice } from '~/types/msal'
import type { SocialSignupMethod } from '~/types/Tracking'

const SOCIAL_SIGNUP_METHODS: readonly SocialSignupMethod[] = ['google', 'facebook']

const MSAL_OAUTH_PENDING_KEY = 'prepeers:msal-oauth-pending' as const
const SIGNUP_PENDING_KEY = 'prepeers:signup-pending-method' as const
const EMAIL_ALREADY_REGISTERED_KEY = 'prepeers:email-already-registered' as const
const ONBOARDING_PENDING_KEY: string = 'prepeers:onboarding-pending'

/**
 * Marque qu'un `loginRedirect` MSAL est en cours (même URL de retour sans `code` encore parsé).
 * @returns {void}
 */
export function setMsalOAuthPending(): void {
  if (!import.meta.client) return
  sessionStorage.setItem(MSAL_OAUTH_PENDING_KEY, '1')
}

/**
 * @returns {boolean} `true` si un retour OAuth MSAL est attendu.
 */
export function peekMsalOAuthPending(): boolean {
  if (!import.meta.client) return false
  return sessionStorage.getItem(MSAL_OAUTH_PENDING_KEY) === '1'
}

/**
 * Efface le marqueur retour OAuth (appeler après traitement du callback ou abandon).
 * @returns {void}
 */
export function clearMsalOAuthPending(): void {
  if (!import.meta.client) return
  sessionStorage.removeItem(MSAL_OAUTH_PENDING_KEY)
}

/**
 * Marque une inscription social en cours (avant `loginRedirect` MSAL).
 * @param {SocialSignupMethod} method - Fournisseur (`google` / `facebook`).
 * @returns {void}
 */
export function setSignupPending(method: SocialSignupMethod): void {
  if (!import.meta.client) return
  sessionStorage.setItem(SIGNUP_PENDING_KEY, method)
}

/**
 * Lit et efface le marqueur d'inscription social (après retour OAuth).
 * @returns {SocialSignupMethod | null} Méthode d'inscription ou `null`.
 */
export function consumeSignupPending(): SocialSignupMethod | null {
  if (!import.meta.client) return null

  const raw: string | null = sessionStorage.getItem(SIGNUP_PENDING_KEY)
  sessionStorage.removeItem(SIGNUP_PENDING_KEY)

  if (raw !== null && SOCIAL_SIGNUP_METHODS.includes(raw as SocialSignupMethod)) {
    return raw as SocialSignupMethod
  }

  return null
}

/**
 * Mémorise que le compte qui se connecte doit passer par l'onboarding.
 * @returns {void}
 */
export function setOnboardingPending(): void {
  if (!import.meta.client) return
  sessionStorage.setItem(ONBOARDING_PENDING_KEY, '1')
}

/**
 * Lit et efface le marqueur d'onboarding posé à la connexion.
 * @returns {boolean} `true` si le compte connecté doit passer par l'onboarding.
 */
export function consumeOnboardingPending(): boolean {
  if (!import.meta.client) return false

  const isOnboardingPending: boolean = sessionStorage.getItem(ONBOARDING_PENDING_KEY) === '1'
  sessionStorage.removeItem(ONBOARDING_PENDING_KEY)
  return isOnboardingPending
}

/**
 * Mémorise qu'une connexion vient d'être refusée parce que l'email appartient déjà à un compte PrePeers,
 * le temps de la déconnexion Entra : la page de connexion l'affiche au retour.
 * @param {string | null} email - Email concerné, s'il est connu.
 * @returns {void}
 */
export function setEmailAlreadyRegisteredNotice(email: string | null): void {
  if (!import.meta.client) return
  const notice: EmailAlreadyRegisteredNotice = { email }
  sessionStorage.setItem(EMAIL_ALREADY_REGISTERED_KEY, JSON.stringify(notice))
}

/**
 * Lit et efface le refus « email déjà inscrit » laissé avant la déconnexion Entra.
 * @returns {EmailAlreadyRegisteredNotice | null} Refus à afficher, ou `null` s'il n'y en a pas.
 */
export function consumeEmailAlreadyRegisteredNotice(): EmailAlreadyRegisteredNotice | null {
  if (!import.meta.client) return null

  const raw: string | null = sessionStorage.getItem(EMAIL_ALREADY_REGISTERED_KEY)
  sessionStorage.removeItem(EMAIL_ALREADY_REGISTERED_KEY)
  if (raw === null) return null

  try {
    const notice: unknown = JSON.parse(raw)
    const email: unknown =
      typeof notice === 'object' && notice !== null && 'email' in notice ? notice.email : null
    return { email: typeof email === 'string' && email !== '' ? email : null }
  } catch {
    return { email: null }
  }
}
